/* ==========================================================================
   ZOMBIE SNACKS — FEED THE ZOMBIE (sections/zombie-munch.liquid)
   <zombie-munch> turns the Zombie Promise art into a tiny game:
     - drag a fruit: it lifts and swings with the pointer; the zombie leans
       toward it and its jaw opens wider the closer it gets, then shivers
       wide open once the fruit is in reach.
     - drop it in reach: the fruit is gulped, the jaw snaps shut and chews
       (squash + stretch, juice burst, a splat left on the art, a sound word,
       a speech-bubble reaction, a haptic buzz where supported).
     - drop it short: it springs home and the zombie complains.
     - tap / click / Enter / Space on a fruit: it is thrown into the mouth.
     - eat all five: finale + button. Nothing is stored, so every page load
       starts with a full bowl (a back/forward-cache restore resets too).
   Sound (Web Audio) is off until the visitor turns it on, unless the
   section's "Sound on by default" is ticked. Each slot plays the supplied
   Zombie sound effect (or a theme-editor override); until a file has
   loaded, or for slots without one, a synthesised stand-in plays.
   Reduced motion keeps the game but skips idle loops,
   shake, particles and splats. Idle CSS motion only runs while on screen.
   ========================================================================== */
(() => {
  if (customElements.get('zombie-munch')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const rand = (lo, hi) => lo + Math.random() * (hi - lo);
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  // slots with a synthesised stand-in when no file is loaded (the originals only)
  const SYNTH_KINDS = new Set(['chomp', 'chew', 'pickup', 'gulp', 'miss', 'finale']);
  // a rotten bite splats sludge instead of the fruit's colour (timed game)
  const ROTTEN_COLOUR = '#A8C62A';
  // Timed game only: zombie voice priorities, so close events don't pile up into noise.
  // Higher wins; at most two voices overlap (a bite + its reward); a bad bite or the result clears the rest.
  const VOICE_PRIORITY = { poke: 1, eat: 2, reward: 2, rotten: 4, finale: 5, timeupLow: 5 };
  const CLEARS_OTHERS = new Set(['rotten', 'finale', 'timeupLow']);
  const round = (n) => Math.round(n * 10) / 10;

  // --zm-jaw, in % of the head height (see zombie-munch.css)
  const JAW_SHUT = -3.9;
  // Pointer feeding is drag-only (9 Oct 2026): a press must travel this far
  // (CSS px) before the fruit is picked up, so a click / tap or a small wobble
  // never moves or feeds it. A click within the window after a press or
  // release on the fruit is the pointer's own click and is ignored.
  const DRAG_THRESHOLD = 12;
  const POINTER_CLICK_WINDOW = 800;
  const JAW_HUNGRY = 1.5;
  const JAW_WIDE = 6.5;

  // A juice splat: a core with spiky arms, a droplet off the end of each arm
  // and a few loose drops (drawn in a 0–100 box)
  function splatMarkup(colour) {
    const n = 18;
    const pts = [];
    let drops = '';
    for (let i = 0; i < n; i += 1) {
      const a = (i / n) * Math.PI * 2 + rand(-0.08, 0.08);
      const arm = i % 2 === 0 && Math.random() < 0.6;
      const r = arm ? rand(34, 46) : rand(19, 26);
      pts.push([50 + Math.cos(a) * r, 50 + Math.sin(a) * r]);
      if (arm && Math.random() < 0.7) {
        const tip = r + rand(5, 10);
        drops += `<circle cx="${round(50 + Math.cos(a) * tip)}" cy="${round(50 + Math.sin(a) * tip)}" r="${round(rand(2.2, 4))}"/>`;
      }
    }
    const mid = (p, q) => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    const start = mid(pts[n - 1], pts[0]);
    let d = `M${round(start[0])} ${round(start[1])}`;
    for (let i = 0; i < n; i += 1) {
      const p = pts[i];
      const m = mid(p, pts[(i + 1) % n]);
      d += `Q${round(p[0])} ${round(p[1])} ${round(m[0])} ${round(m[1])}`;
    }
    for (let i = 0; i < 6; i += 1) {
      const a = rand(0, Math.PI * 2);
      const r = rand(40, 58);
      drops += `<circle cx="${round(50 + Math.cos(a) * r)}" cy="${round(50 + Math.sin(a) * r)}" r="${round(rand(1.4, 3.6))}"/>`;
    }
    return `<g fill="${colour}"><path d="${d}Z"/>${drops}</g>`;
  }

  class ZombieMunch extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      const $ = (selector) => this.querySelector(selector);
      this.stage = $('[data-zm-stage]');
      this.head = $('[data-zm-head]');
      this.face = $('[data-zm-face]');
      this.mouth = $('[data-zm-mouth]');
      if (!this.stage || !this.head || !this.face || !this.mouth) return;

      this.shaker = $('[data-zm-shaker]');
      this.fx = $('[data-zm-fx]');
      this.splats = $('[data-zm-splats]');
      this.bubble = $('[data-zm-bubble]');
      this.count = $('[data-zm-count]');
      this.pips = Array.from(this.querySelectorAll('[data-zm-pip]'));
      this.liveRegion = $('[data-zm-live]');
      this.hand = $('[data-zm-hand]');
      this.finale = $('[data-zm-finale]');
      this.soundButton = $('[data-zm-sound]');
      // replay lives only in the finale (3 Oct 2026: nothing over the fruit while feeding)
      this.replayButtons = Array.from(this.querySelectorAll('[data-zm-replay]'));
      this.fruits = Array.from(this.querySelectorAll('[data-zm-fruit]'));
      this.total = this.fruits.length;

      try {
        this.lines = JSON.parse($('[data-zm-lines]').textContent);
      } catch (error) {
        this.lines = {};
      }
      this.lines.reactions = (this.lines.reactions || []).map((line) => line.trim()).filter(Boolean);
      // built-in gameplay words: snippets/zombie-munch-copy.liquid (one place to swap them)
      try {
        this.copy = JSON.parse($('[data-zm-copy]').textContent);
      } catch (error) {
        this.copy = {};
      }

      this.eaten = 0;
      this.jaw = 0;
      this.drag = null;
      this.frame = 0;
      this.flights = new Set();
      this.timers = new Set();
      this.interacted = false;
      this.soundOn = Boolean(this.lines.soundOn) && Boolean(this.soundButton);
      this.volume = clamp(Number(this.lines.volume) || 0.8, 0.05, 1);
      // every slot is a list of files (variations); a single string still works
      this.soundFiles = this.lines.sounds || {};
      this.samples = {};
      this.lastPlayed = {};

      this.abort = new AbortController();
      const on = { signal: this.abort.signal };
      this.fruits.forEach((fruit) => {
        fruit.addEventListener('pointerdown', (event) => this.grab(event, fruit), on);
        fruit.addEventListener('pointerup', () => { fruit.zmPressedAt = performance.now(); }, on);
        fruit.addEventListener('click', (event) => this.onFruitClick(fruit, event), on);
      });
      this.head.addEventListener('click', () => {
        this.unlockAudio();
        this.poke();
      }, on);
      if (this.soundButton) this.soundButton.addEventListener('click', () => this.toggleSound(), on);
      // bound once here; replay() reuses reset(), which never adds listeners
      this.replayButtons.forEach((button) => button.addEventListener('click', () => this.replay(), on));
      window.addEventListener('pageshow', (event) => { if (event.persisted) this.reset(); }, on);

      // Idle motion + the demo hand only while the stage is on screen;
      // the benefit stickers slap on the first time they scroll in.
      const benefits = $('.zm__benefits');
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.stage) this.setLive(entry.isIntersecting);
          else if (entry.isIntersecting) {
            this.classList.add('is-seen');
            this.io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.2 });
      this.io.observe(this.stage);
      if (benefits) this.io.observe(benefits);
      else this.classList.add('is-seen');

      this.ro = new ResizeObserver(() => { if (this.handAnim) this.showHand(); });
      this.ro.observe(this.stage);

      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.abort = null;
      if (this.drag) this.drag.stop.abort();
      this.drag = null;
      this.io.disconnect();
      this.ro.disconnect();
      cancelAnimationFrame(this.frame);
      this.flights.forEach((id) => cancelAnimationFrame(id));
      this.timers.forEach((id) => clearTimeout(id));
      clearTimeout(this.sayTimer);
      this.hideHand();
      if (this.ctx) this.ctx.close();
    }

    later(fn, ms) {
      const id = setTimeout(() => {
        this.timers.delete(id);
        fn();
      }, ms);
      this.timers.add(id);
    }

    setLive(visible) {
      this.classList.toggle('is-live', visible);
      if (!visible) {
        if (this.handAnim) this.hideHand();
        return;
      }
      if (this.soundOn) this.prefetchSounds();
      if (!this.interacted && this.eaten < this.total) this.later(() => this.showHand(), 1200);
    }

    // ---- Geometry ---------------------------------------------------------
    mouthPoint() {
      const box = this.mouth.getBoundingClientRect();
      return { x: box.left, y: box.top };
    }

    colourOf(fruit) {
      return getComputedStyle(fruit).getPropertyValue('--c').trim() || '#FF0ADC';
    }

    // The zombie reacts to a fruit at client point (x, y). Returns true when
    // the fruit is close enough to be eaten.
    aim(x, y) {
      const m = this.mouthAt || this.mouthPoint();
      const headSize = this.head.offsetWidth;
      const stageSize = this.stage.offsetWidth;
      const dx = x - m.x;
      const dy = y - m.y;
      const dist = Math.hypot(dx, dy);
      const reach = headSize * 0.55;
      const far = Math.max(reach * 1.5, stageSize * 0.7);
      const near = clamp(1 - (dist - reach) / (far - reach), 0, 1);
      const inReach = dist < reach;

      this.setJaw(lerp(this.jaw, lerp(JAW_HUNGRY, JAW_WIDE, near * near), 0.35));

      const pull = near * headSize * 0.1;
      const k = dist ? pull / dist : 0;
      const turn = clamp((dx / stageSize) * 22, -14, 14) * (0.4 + near * 0.6);
      this.head.style.transform = `translate3d(${round(dx * k)}px, ${round(dy * k)}px, 0) rotate(${round(turn)}deg) scale(${(1 + near * 0.08).toFixed(3)})`;

      if (inReach !== this.inReach) {
        this.inReach = inReach;
        this.classList.toggle('is-in-reach', inReach);
      }
      return inReach;
    }

    setJaw(value) {
      this.jaw = value;
      this.face.style.setProperty('--zm-jaw', value.toFixed(2));
    }

    easeJaw(to, duration) {
      const from = this.jaw;
      this.setJaw(to);
      if (reduceMotion.matches) return;
      this.face.animate([{ '--zm-jaw': from }, { '--zm-jaw': to }], { duration, easing: 'ease-out' });
    }

    // ---- Dragging ---------------------------------------------------------
    grab(event, fruit) {
      this.pointerType = event.pointerType;
      fruit.zmPressedAt = performance.now();
      this.unlockAudio();
      if (fruit.dataset.state === 'expiring' && this.game) this.game.rescue(fruit);
      if (this.drag || fruit.dataset.state || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const box = fruit.getBoundingClientRect();
      const drag = {
        fruit,
        id: event.pointerId,
        x0: event.clientX,
        y0: event.clientY,
        x: event.clientX,
        y: event.clientY,
        px: event.clientX,
        cx: box.left + box.width / 2,
        cy: box.top + box.height / 2,
        tilt: 0,
        moved: false,
        stop: new AbortController()
      };
      this.drag = drag;
      try { fruit.setPointerCapture(event.pointerId); } catch (error) { /* not capturable: moves still arrive while over the fruit */ }
      const on = { signal: drag.stop.signal };
      fruit.addEventListener('pointermove', (e) => this.onMove(e), on);
      fruit.addEventListener('pointerup', (e) => this.onUp(e, false), on);
      fruit.addEventListener('pointercancel', (e) => this.onUp(e, true), on);
    }

    onMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      drag.x = event.clientX;
      drag.y = event.clientY;
      if (!drag.moved) {
        if (Math.hypot(drag.x - drag.x0, drag.y - drag.y0) < DRAG_THRESHOLD) return;
        drag.moved = true;
        this.pickUp(drag.fruit);
      }
      if (!this.frame) this.frame = requestAnimationFrame(this.tick);
    }

    // Runs every frame while a fruit is held: follow the pointer, swing with
    // horizontal speed, and let the zombie track it.
    tick = () => {
      this.frame = 0;
      const drag = this.drag;
      if (!drag || !drag.moved) return;
      const dx = drag.x - drag.x0;
      const dy = drag.y - drag.y0;
      drag.tilt = lerp(drag.tilt, clamp((drag.x - drag.px) * 1.5, -30, 30), 0.2);
      drag.px = drag.x;
      drag.fruit.style.transform = `translate3d(${dx}px, ${dy}px, 0) rotate(${round(drag.tilt)}deg) scale(1.15)`;
      drag.fruit.zmOffset = { x: dx, y: dy, tilt: drag.tilt };
      this.aim(drag.cx + dx, drag.cy + dy);
      this.frame = requestAnimationFrame(this.tick);
    };

    onUp(event, cancelled) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      drag.stop.abort();
      this.drag = null;
      this.unlockAudio();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      if (!drag.moved) return; // a click / tap (or a wobble under the threshold): nothing happens
      this.dragEndedAt = performance.now();
      const inReach = this.aim(drag.cx + drag.x - drag.x0, drag.cy + drag.y - drag.y0);
      if (inReach && !cancelled) this.eat(drag.fruit);
      else this.miss(drag.fruit);
    }

    // Client feedback 9 Oct 2026: pointer users must DRAG the fruit into the
    // mouth — a click or tap on a fruit never feeds it. Any click that follows
    // a press on the fruit (mouse, touch, pen) is ignored; a click without one
    // comes from the keyboard (Enter / Space on the focused fruit button) or
    // assistive tech, and stays as the accessible way to feed.
    onFruitClick(fruit, event) {
      if (performance.now() - (fruit.zmPressedAt || 0) < POINTER_CLICK_WINDOW) return;
      if (performance.now() - (this.dragEndedAt || 0) < 350) return;
      this.unlockAudio();
      this.feed(fruit);
    }

    pickUp(fruit) {
      this.started();
      fruit.getAnimations().forEach((animation) => animation.cancel());
      fruit.classList.remove('is-returning');
      fruit.classList.add('is-held');
      this.classList.add('is-feeding');
      this.mouthAt = this.mouthPoint();
      this.say(this.lines.grab);
      this.sfx('pickup');
    }

    started() {
      if (this.interacted) return;
      this.interacted = true;
      this.hideHand();
    }

    miss(fruit) {
      fruit.classList.remove('is-held');
      fruit.classList.add('is-returning');
      fruit.style.transform = '';
      fruit.zmOffset = null;
      this.later(() => fruit.classList.remove('is-returning'), 700);
      this.settle();
      this.say(this.lines.miss, 1800);
      this.sfx('miss');
      if (this.game) this.game.onMiss(fruit);
    }

    // Zombie back to rest after a drop short of the mouth
    settle() {
      this.classList.remove('is-feeding', 'is-in-reach');
      this.inReach = false;
      this.mouthAt = null;
      this.head.style.transform = '';
      this.easeJaw(0, 280);
    }

    // ---- Keyboard / assistive tech: throw the fruit into the mouth ----------
    feed(fruit) {
      if (fruit.dataset.state === 'expiring' && this.game) this.game.rescue(fruit);
      if (fruit.dataset.state || this.drag) return;
      this.started();
      fruit.dataset.state = 'flying';
      fruit.getAnimations().forEach((animation) => animation.cancel());
      fruit.classList.remove('is-returning');
      fruit.classList.add('is-held');
      this.classList.add('is-feeding');
      this.mouthAt = this.mouthPoint();

      const box = fruit.getBoundingClientRect();
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      const tx = this.mouthAt.x - cx;
      const ty = this.mouthAt.y - cy;
      const quick = reduceMotion.matches;
      const duration = quick ? 200 : 560;
      const lift = quick ? 0 : Math.min(this.stage.offsetHeight * 0.22, 40 + Math.abs(tx) * 0.3);
      const spin = tx < 0 ? -300 : 300;
      const t0 = performance.now();
      this.say(this.lines.grab);
      this.sfx('pickup');

      const step = (now) => {
        this.flights.delete(id);
        const t = clamp((now - t0) / duration, 0, 1);
        const e = t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;
        const x = tx * e;
        const y = ty * e - Math.sin(Math.PI * t) * lift;
        const tilt = quick ? 0 : spin * e;
        fruit.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${round(tilt)}deg) scale(${(1.15 - 0.35 * e).toFixed(3)})`;
        fruit.zmOffset = { x, y, tilt };
        this.aim(cx + x, cy + y);
        if (t < 1) {
          id = requestAnimationFrame(step);
          this.flights.add(id);
        } else {
          this.eat(fruit);
        }
      };
      let id = requestAnimationFrame(step);
      this.flights.add(id);
    }

    // ---- Eating -----------------------------------------------------------
    eat(fruit) {
      // timed game only: feeding a rotten fruit gets its own bite (sound, colour, word)
      const rotten = Boolean(this.game && this.game.isRotten(fruit));
      fruit.dataset.state = 'eaten';
      const quick = reduceMotion.matches;
      const hadFocus = document.activeElement === fruit;
      const colour = rotten ? ROTTEN_COLOUR : this.colourOf(fruit);
      const m = this.mouthAt || this.mouthPoint();
      const box = fruit.getBoundingClientRect();
      const offset = fruit.zmOffset || { x: 0, y: 0, tilt: 0 };
      const gx = m.x - (box.left + box.width / 2 - offset.x);
      const gy = m.y - (box.top + box.height / 2 - offset.y);
      const sink = this.head.offsetHeight * 0.06;

      const gulp = fruit.animate([
        { transform: fruit.style.transform || 'none', opacity: 1 },
        { transform: `translate3d(${gx}px, ${gy}px, 0) rotate(${round(offset.tilt + 40)}deg) scale(0.55)`, opacity: 1, offset: 0.55 },
        { transform: `translate3d(${gx}px, ${gy + sink}px, 0) rotate(${round(offset.tilt + 90)}deg) scale(0.1)`, opacity: 0 }
      ], { duration: quick ? 160 : 240, easing: 'cubic-bezier(0.5, 0, 0.75, 0)', fill: 'forwards' });
      gulp.onfinish = () => { fruit.hidden = true; };
      this.later(() => this.chomp(fruit, colour, rotten), quick ? 60 : 130);

      if (this.game) { this.game.onEat(fruit, hadFocus); return; }

      this.eaten += 1;
      const pip = this.pips[this.eaten - 1];
      if (pip) {
        pip.style.setProperty('--c', colour);
        pip.classList.add('is-full');
      }
      if (this.count) this.count.textContent = `${this.eaten}/${this.total}`;
      this.announce(
        (this.lines.announce || '')
          .replace('[fruit]', fruit.dataset.name.charAt(0).toUpperCase() + fruit.dataset.name.slice(1))
          .replace('[count]', this.eaten)
          .replace('[total]', this.total)
      );

      if (hadFocus) {
        const next = this.fruits.find((other) => !other.dataset.state);
        if (next) next.focus({ preventScroll: true });
      }
      if (this.eaten >= this.total) this.later(() => this.finish(hadFocus), quick ? 400 : 1350);
    }

    chomp(fruit, colour, rotten = false) {
      const quick = reduceMotion.matches;
      this.classList.remove('is-feeding', 'is-in-reach');
      this.classList.add('is-chewing');
      this.inReach = false;
      this.mouthAt = null;
      this.head.style.transform = '';

      const from = this.jaw;
      this.setJaw(0);
      if (this.chewAnim) this.chewAnim.cancel();
      const frames = quick ? [
        { '--zm-jaw': from },
        { '--zm-jaw': JAW_SHUT, offset: 0.4 },
        { '--zm-jaw': 0 }
      ] : [
        { '--zm-jaw': from, transform: 'none' },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.12, 0.86)', offset: 0.07 },
        { '--zm-jaw': 2.4, transform: 'scale(0.95, 1.06) rotate(-4deg)', offset: 0.22 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.07, 0.93) rotate(0deg)', offset: 0.34 },
        { '--zm-jaw': 2, transform: 'scale(0.97, 1.04) rotate(4deg)', offset: 0.48 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.06, 0.94) rotate(0deg)', offset: 0.6 },
        { '--zm-jaw': 1.4, transform: 'scale(0.98, 1.03) rotate(-3deg)', offset: 0.72 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.04, 0.96) rotate(0deg)', offset: 0.82 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(0.94, 1.1) translateY(-4%)', offset: 0.9 },
        { '--zm-jaw': 0, transform: 'none' }
      ];
      const duration = quick ? 260 : 1250;
      const chew = this.face.animate(frames, { duration, easing: 'ease-in-out' });
      this.chewAnim = chew;
      chew.onfinish = () => {
        if (this.chewAnim !== chew) return;
        this.chewAnim = null;
        this.classList.remove('is-chewing');
      };

      if (!quick) {
        this.shake();
        this.burst([colour], 1, 1);
        this.splat(colour);
        this.popWord(rotten ? this.copy.rotten_word : fruit.dataset.word, colour);
        const activated = !navigator.userActivation || navigator.userActivation.hasBeenActive;
        if (this.pointerType === 'touch' && navigator.vibrate && activated) navigator.vibrate(24);
      }
      if (rotten && this.hasSample('rotten')) this.sfx('rotten');
      else if (this.hasSample('eat')) this.sfx('eat');
      else {
        this.sfx('chomp');
        [0.3, 0.46, 0.62, 0.78].forEach((k) => this.sfx('chew', (k * duration) / 1000));
        this.sfx('gulp', (0.88 * duration) / 1000);
      }
      if (rotten) return; // the timed game gives the rotten reaction
      if (this.game && this.game.holdBubbleUntil > performance.now()) return; // keep its BLEURGH! up
      if (this.eaten < this.total && this.lines.reactions.length) this.say(pick(this.lines.reactions), 2200);
    }

    finish(focusButton) {
      this.classList.add('is-full');
      this.say(this.lines.full);
      this.announce(this.lines.announceFull);
      // the supplied finale waits for the last bite to finish instead of talking over it
      this.sfx('finale', this.lastPlayed.eat ? this.lastPlayed.eat.duration : 0.1);
      const quick = reduceMotion.matches;
      if (!quick) {
        this.face.animate([
          { transform: 'none' },
          { transform: 'rotate(-12deg) scale(1.12)', offset: 0.2 },
          { transform: 'rotate(10deg) scale(1.08)', offset: 0.4 },
          { transform: 'rotate(-7deg) scale(1.1)', offset: 0.6 },
          { transform: 'rotate(4deg) scale(1.04)', offset: 0.8 },
          { transform: 'none' }
        ], { duration: 1100, easing: 'ease-in-out' });
        const colours = this.fruits.map((fruit) => this.colourOf(fruit));
        this.burst(colours, 2.2, 1.35);
        this.later(() => this.burst(colours, 1.4, 1.1), 260);
      }
      if (this.finale) {
        this.finale.hidden = false;
        this.finale.animate([
          { opacity: 0, transform: 'translateY(24px) scale(0.9)' },
          { opacity: 1, transform: 'none' }
        ], { duration: quick ? 1 : 520, delay: quick ? 0 : 250, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'backwards' });
        const button = this.finale.querySelector('a');
        if (focusButton && button) button.focus({ preventScroll: true });
      }
    }

    // Clicking the head: it snaps at you
    poke() {
      if (this.drag || this.chewAnim || this.flights.size) return;
      const quick = reduceMotion.matches;
      this.classList.add('is-chewing');
      const chew = this.face.animate(quick ? [
        { '--zm-jaw': 0 }, { '--zm-jaw': JAW_SHUT, offset: 0.5 }, { '--zm-jaw': 0 }
      ] : [
        { '--zm-jaw': 0, transform: 'none' },
        { '--zm-jaw': 4, transform: 'scale(1.06) rotate(-3deg)', offset: 0.2 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.1, 0.9)', offset: 0.35 },
        { '--zm-jaw': 3, transform: 'rotate(3deg)', offset: 0.55 },
        { '--zm-jaw': JAW_SHUT, transform: 'scale(1.08, 0.92)', offset: 0.7 },
        { '--zm-jaw': 0, transform: 'none' }
      ], { duration: quick ? 240 : 700, easing: 'ease-in-out' });
      this.chewAnim = chew;
      chew.onfinish = () => {
        if (this.chewAnim !== chew) return;
        this.chewAnim = null;
        this.classList.remove('is-chewing');
      };
      this.say(this.eaten >= this.total ? this.lines.full : this.lines.idle);
      this.zombieSound();
    }

    // ---- Effects ----------------------------------------------------------
    shake() {
      if (!this.shaker) return;
      this.shaker.animate([
        { transform: 'none' },
        { transform: 'translate(-1.2%, 0.8%) rotate(-0.6deg)' },
        { transform: 'translate(1.2%, -0.6%) rotate(0.6deg)' },
        { transform: 'translate(-0.8%, -0.4%)' },
        { transform: 'translate(0.4%, 0.4%)' },
        { transform: 'none' }
      ], { duration: 320, easing: 'ease-out' });
    }

    // Juice drops, white sparks and fruit chunks thrown out of the mouth on
    // a simple ballistic arc (5 keyframes, compositor-only).
    burst(colours, amount, power) {
      if (!this.fx) return;
      const box = this.fx.getBoundingClientRect();
      const m = this.mouthPoint();
      const ox = m.x - box.left;
      const oy = m.y - box.top;
      const w = this.stage.offsetWidth;
      const gravity = w * 2.2;
      const parts = [];
      for (let i = 0; i < Math.round(18 * amount); i += 1) parts.push(['drop', pick(colours), rand(0.014, 0.032) * w]);
      for (let i = 0; i < Math.round(6 * amount); i += 1) parts.push(['spark', '#fff', rand(0.02, 0.04) * w]);
      for (let i = 0; i < Math.round(5 * amount); i += 1) parts.push(['chunk', pick(colours), rand(0.018, 0.03) * w]);

      parts.forEach(([kind, colour, size]) => {
        const el = document.createElement('i');
        el.className = `zm-p zm-p--${kind}`;
        el.style.setProperty('--c', colour);
        const height = kind === 'spark' ? size * 0.3 : size;
        const width = kind === 'drop' ? size * 0.75 : size;
        el.style.width = `${round(width)}px`;
        el.style.height = `${round(height)}px`;
        this.fx.appendChild(el);

        const angle = kind === 'chunk' ? rand(-Math.PI, 0.2) : rand(-Math.PI * 0.95, -Math.PI * 0.05);
        const speed = rand(0.35, 0.8) * w * power;
        const life = rand(0.55, 0.95);
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;
        const spin = rand(-540, 540);
        const frames = [0, 0.25, 0.5, 0.75, 1].map((k) => {
          const t = k * life;
          const x = ox + vx * t - width / 2;
          const y = oy + vy * t + 0.5 * gravity * t * t - height / 2;
          const rot = kind === 'chunk' ? spin * k : (Math.atan2(vy + gravity * t, vx) * 180) / Math.PI + (kind === 'drop' ? -90 : 0);
          return {
            transform: `translate3d(${round(x)}px, ${round(y)}px, 0) rotate(${round(rot)}deg) scale(${(1 - k * 0.55).toFixed(2)})`,
            opacity: k < 0.7 ? 1 : (1 - k) / 0.3
          };
        });
        el.animate(frames, { duration: life * 1000, easing: 'linear', fill: 'forwards' }).onfinish = () => el.remove();
      });
    }

    // A juice splat left on the art around the head (stays until reload)
    splat(colour) {
      if (!this.splats) return;
      const box = this.stage.getBoundingClientRect();
      const m = this.mouthPoint();
      const w = box.width;
      const h = box.height;
      const mx = (m.x - box.left) / w;
      const my = (m.y - box.top) / h;
      const spread = rand(0, Math.PI * 2);
      for (let i = 0; i < 2; i += 1) {
        // two splats roughly opposite each other, clear of the head
        const a = spread + i * Math.PI + rand(-0.6, 0.6);
        const r = rand(0.22, 0.38);
        const size = rand(0.1, 0.17);
        const x = clamp(mx + Math.cos(a) * r, 0.08, 0.92);
        const y = clamp(my + (Math.sin(a) * r * w) / h, 0.08, 0.92);
        const turn = rand(-180, 180);
        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('class', 'zm-splat');
        svg.setAttribute('viewBox', '0 0 100 100');
        svg.setAttribute('aria-hidden', 'true');
        svg.style.width = `${(size * 100).toFixed(2)}%`;
        svg.style.left = `${((x - size / 2) * 100).toFixed(2)}%`;
        svg.style.top = `${((y - (size * w) / h / 2) * 100).toFixed(2)}%`;
        svg.innerHTML = splatMarkup(colour);
        this.splats.appendChild(svg);
        svg.animate([
          { transform: `rotate(${round(turn)}deg) scale(0)`, opacity: 1 },
          { transform: `rotate(${round(turn)}deg) scale(1.18)`, opacity: 0.95, offset: 0.6 },
          { transform: `rotate(${round(turn)}deg) scale(1)`, opacity: 0.85 }
        ], { duration: 380, delay: 60 + i * 70, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1.2)', fill: 'both' });
      }
      while (this.splats.children.length > 14) this.splats.firstElementChild.remove();
    }

    popWord(word, colour) {
      if (!word || !this.fx) return;
      const el = document.createElement('span');
      el.className = 'zm-pop';
      el.textContent = word;
      el.style.setProperty('--c', colour);
      this.fx.appendChild(el);
      // up and to the left of the head: the speech bubble owns the right
      const box = this.fx.getBoundingClientRect();
      const m = this.mouthPoint();
      const side = -1;
      const x = m.x - box.left - this.head.offsetWidth * 0.42;
      const y = m.y - box.top - this.head.offsetHeight * 0.5;
      const rise = box.height * 0.1;
      const rot = rand(-14, -4);
      const at = (dx, dy, deg, scale) => `translate(${round(x + dx)}px, ${round(y + dy)}px) translate(-50%, -50%) rotate(${round(deg)}deg) scale(${scale})`;
      el.animate([
        { transform: at(0, 0, rot, 0.2), opacity: 0 },
        { transform: at(0, 0, rot, 1.3), opacity: 1, offset: 0.18 },
        { transform: at(0, 0, rot, 1), opacity: 1, offset: 0.3 },
        { transform: at(side * 16, -rise, rot + side * 6, 1), opacity: 1, offset: 0.75 },
        { transform: at(side * 22, -rise * 1.4, rot + side * 8, 0.9), opacity: 0 }
      ], { duration: 1000, easing: 'ease-out', fill: 'forwards' }).onfinish = () => el.remove();
    }

    say(text, holdFor) {
      if (!this.bubble || !text) return;
      clearTimeout(this.sayTimer);
      if (this.bubble.textContent !== text) {
        this.bubble.textContent = text;
        if (!reduceMotion.matches) {
          this.bubble.animate([{ scale: 0.7 }, { scale: 1.12, offset: 0.6 }, { scale: 1 }], { duration: 300, easing: 'ease-out' });
        }
      }
      if (holdFor) {
        this.sayTimer = setTimeout(() => this.say(this.eaten >= this.total ? this.lines.full : this.lines.idle), holdFor);
      }
    }

    announce(text) {
      if (!this.liveRegion || !text) return;
      this.liveRegion.textContent = '';
      this.later(() => { this.liveRegion.textContent = text; }, 60);
    }

    // ---- Demo hand: shows the drag until the visitor tries it -------------
    showHand() {
      if (!this.hand || this.interacted || this.eaten >= this.total || !this.classList.contains('is-live')) return;
      const fruit = this.fruits.find((other) => !other.dataset.state);
      if (!fruit) return;
      this.hideHand();
      const stage = this.stage.getBoundingClientRect();
      const f = fruit.getBoundingClientRect();
      const m = this.mouthPoint();
      const fx = f.left + f.width / 2 - stage.left;
      const fy = f.top + f.height / 2 - stage.top;
      const mx = m.x - stage.left;
      const my = m.y - stage.top;
      this.hand.hidden = false;
      const at = (x, y, scale) => `translate(${round(x)}px, ${round(y)}px) scale(${scale})`;

      if (reduceMotion.matches) {
        this.hand.style.transform = at(fx, fy, 1);
        this.hand.style.opacity = '1';
        return;
      }
      const timing = { duration: 2800, iterations: Infinity, easing: 'ease-in-out' };
      this.handAnim = this.hand.animate([
        { transform: at(fx, fy + 20, 1.2), opacity: 0 },
        { transform: at(fx, fy, 1.2), opacity: 1, offset: 0.12 },
        { transform: at(fx, fy, 0.9), opacity: 1, offset: 0.22 },
        { transform: at(mx, my, 0.9), opacity: 1, offset: 0.62 },
        { transform: at(mx, my, 1.1), opacity: 0, offset: 0.76 },
        { transform: at(mx, my, 1.1), opacity: 0 }
      ], timing);
      // the fruit gets tugged a little way along, then springs back
      const pull = `translate(${round((mx - fx) * 0.16)}px, ${round((my - fy) * 0.16)}px) rotate(6deg) scale(1.1)`;
      this.wiggle = fruit.animate([
        { transform: 'none' },
        { transform: 'none', offset: 0.18 },
        { transform: 'rotate(-7deg) scale(1.1)', offset: 0.22 },
        { transform: pull, offset: 0.4 },
        { transform: 'rotate(-4deg)', offset: 0.5 },
        { transform: 'none', offset: 0.56 },
        { transform: 'none' }
      ], timing);
    }

    hideHand() {
      if (this.handAnim) this.handAnim.cancel();
      if (this.wiggle) this.wiggle.cancel();
      this.handAnim = null;
      this.wiggle = null;
      if (this.hand) {
        this.hand.hidden = true;
        this.hand.style.transform = '';
        this.hand.style.opacity = '';
      }
    }

    // ---- Back to a full bowl (bfcache restore) ----------------------------
    reset() {
      this.flights.forEach((id) => cancelAnimationFrame(id));
      this.flights.clear();
      this.timers.forEach((id) => clearTimeout(id));
      this.timers.clear();
      if (this.chewAnim) this.chewAnim.cancel();
      this.chewAnim = null;
      this.fruits.forEach((fruit) => {
        fruit.getAnimations().forEach((animation) => animation.cancel());
        fruit.hidden = false;
        delete fruit.dataset.state;
        fruit.style.transform = '';
        fruit.zmOffset = null;
        fruit.classList.remove('is-held', 'is-returning');
      });
      this.pips.forEach((pip) => pip.classList.remove('is-full'));
      this.eaten = 0;
      if (this.count) this.count.textContent = `0/${this.total}`;
      if (this.splats) this.splats.replaceChildren();
      if (this.fx) this.fx.replaceChildren();
      if (this.finale) this.finale.hidden = true;
      this.drag = null;
      this.inReach = false;
      this.mouthAt = null;
      this.dragEndedAt = 0;
      this.hideHand && this.hideHand();
      this.silence();
      this.classList.remove('is-full', 'is-feeding', 'is-chewing', 'is-in-reach');
      this.head.style.transform = '';
      this.setJaw(0);
      this.interacted = false;
      this.say(this.lines.idle);
      if (this.classList.contains('is-live')) this.later(() => this.showHand(), 1200);
    }

    // ---- Replay (client feedback 2 Oct 2026) -------------------------------
    // Refills the bowl in place: fruit, score, pips, splats, finale, timers and
    // in-flight sounds all reset; focus returns to the first fruit so keyboard
    // and screen-reader users can carry straight on.
    replay() {
      if (this.game) { this.game.restart(); return; }
      this.reset();
      this.announce(this.lines.announceReplay || this.copy.sr_replay);
      const first = this.fruits.find((fruit) => !fruit.hidden);
      if (first) first.focus({ preventScroll: true });
    }

    // Cut off anything still sounding (a chew or the finale) by swapping the
    // output bus: in-flight nodes stay attached to the old, disconnected one.
    silence() {
      if (!this.ctx || !this.master) return;
      try { this.master.disconnect(); } catch (error) { /* already detached */ }
      this.voices = [];
      this.lastPickup = null;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    }

    // ---- Sound ------------------------------------------------------------
    // Every slot plays the merchant's own file when one is set in the theme
    // editor (Content → Files name or URL) and falls back to a synthesised
    // sound otherwise, or while the file is still loading / if it fails.
    toggleSound() {
      this.soundOn = !this.soundOn;
      this.soundButton.setAttribute('aria-pressed', String(this.soundOn));
      if (this.soundOn) {
        this.prefetchSounds();
        this.audio();
        // give the files a moment to decode so the confirmation is the real voice
        const wait = new Promise((resolve) => setTimeout(resolve, 600));
        Promise.race([this.samplesReady || wait, wait]).then(() => this.zombieSound());
      }
    }

    // Touching the zombie: one of the two supplied voices at random
    zombieSound() {
      // one of the supplied voices at random (never the same twice running)
      if (this.hasSample('poke')) {
        this.sfx('poke');
        return;
      }
      this.sfx('chomp', 0.24);
      this.sfx('chew', 0.49);
    }

    // Browsers (iOS especially) only let audio start inside a tap / click, so
    // this runs synchronously in those handlers before any delayed sound.
    unlockAudio() {
      if (this.soundOn) this.audio();
    }

    audio() {
      if (!this.ctx) {
        const Context = window.AudioContext || window.webkitAudioContext;
        if (!Context) return null;
        this.ctx = new Context();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.volume;
        this.master.connect(this.ctx.destination);
        const length = Math.floor(this.ctx.sampleRate * 0.5);
        this.noise = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
        const data = this.noise.getChannelData(0);
        for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
        this.loadSamples();
      }
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return this.ctx;
    }

    // Download the files early (no audio context needed yet) ...
    prefetchSounds() {
      if (this.soundData) return;
      this.soundData = {};
      Object.entries(this.soundFiles).forEach(([kind, files]) => {
        const urls = (Array.isArray(files) ? files : [files]).filter(Boolean);
        if (!urls.length) return;
        this.soundData[kind] = urls.map((url) => {
          const pending = fetch(url).then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.arrayBuffer();
          });
          pending.catch(() => {});
          return pending;
        });
      });
    }

    // ... and decode them once there is one. Each slot keeps every variation
    // that decodes; one that is missing or unreadable is simply skipped.
    loadSamples() {
      this.prefetchSounds();
      const jobs = [];
      Object.entries(this.soundData).forEach(([kind, list]) => {
        const buffers = [];
        list.forEach((pending, index) => jobs.push(pending
          .then((data) => this.ctx.decodeAudioData(data))
          .then((buffer) => {
            buffers[index] = buffer;
            this.samples[kind] = buffers.filter(Boolean);
          })
          .catch(() => { /* this variation stays out; an empty slot falls back to synthesis */ })));
      });
      this.samplesReady = Promise.allSettled(jobs);
    }

    hasSample(kind) {
      return Boolean(this.samples[kind] && this.samples[kind].length);
    }

    // a random variation; in the timed game never the same one twice in a row
    // (Home keeps its original plain random pick)
    pickSample(kind) {
      const list = this.samples[kind];
      if (!list || !list.length) return null;
      if (list.length === 1) return list[0];
      if (!this.game) return pick(list);
      const options = list.filter((buffer) => buffer !== this.lastPlayed[kind]);
      return pick(options);
    }

    // ---- Timed game: voice priority (Home never uses this) ------------------
    claimVoice(kind, prio, t, duration) {
      this.voices = (this.voices || []).filter((v) => v.end > this.ctx.currentTime + 0.02);
      const louder = this.voices.some((v) => v.prio > prio && v.end > t + 0.05);
      if (louder) return false;
      if (CLEARS_OTHERS.has(kind)) {
        this.voices.forEach((v) => this.fadeVoice(v, t));
        this.voices = [];
      } else if (this.voices.length >= 2) {
        // keep the newest: fade the oldest, quietest voice out of the way
        const oldest = this.voices.slice().sort((a, b) => a.prio - b.prio || a.start - b.start)[0];
        this.fadeVoice(oldest, t);
        this.voices = this.voices.filter((v) => v !== oldest);
      }
      return true;
    }

    fadeVoice(voice, t) {
      try {
        voice.gain.gain.setValueAtTime(voice.gain.gain.value, t);
        voice.gain.gain.linearRampToValueAtTime(0.0001, t + 0.08);
        voice.src.stop(t + 0.1);
      } catch (error) { /* already stopped */ }
    }

    sfx(kind, when = 0) {
      if (!this.soundOn) return;
      const ctx = this.audio();
      if (!ctx) return;
      const t = ctx.currentTime + when;

      const sample = this.pickSample(kind);
      if (sample) {
        // timed game: give way to a more important voice instead of stacking under it
        const prio = this.game ? VOICE_PRIORITY[kind] : undefined;
        if (prio !== undefined && !this.claimVoice(kind, prio, t, sample.duration)) return null;
        this.lastPlayed[kind] = sample;
        const src = ctx.createBufferSource();
        src.buffer = sample;
        const gain = ctx.createGain();
        src.connect(gain).connect(this.master);
        src.start(t);
        if (prio !== undefined) this.voices.push({ kind, prio, gain, src, start: t, end: t + sample.duration });
        // timed game: a new pickup cuts the previous one (rapid grabs)
        if (this.game && kind === 'pickup') { if (this.lastPickup) this.fadeVoice(this.lastPickup, t); this.lastPickup = { gain, src }; }
        return sample;
      }
      // no file for this slot: only the original slots have a synthesised stand-in;
      // the timed-game slots (rotten, combo, golden, countdown, timeup) stay silent
      if (!SYNTH_KINDS.has(kind)) return null;

      const out = ctx.createGain();
      out.gain.value = 0.6;
      out.connect(this.master);
      const envelope = (gain, peak, attack, decay, start = t) => {
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(peak, start + attack);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + attack + decay);
      };
      const crunch = (bursts, loudness) => {
        // gritty bursts of band-passed noise
        for (let i = 0; i < bursts; i += 1) {
          const start = t + i * rand(0.012, 0.03);
          const src = ctx.createBufferSource();
          src.buffer = this.noise;
          const band = ctx.createBiquadFilter();
          band.type = 'bandpass';
          band.frequency.value = rand(700, 2600);
          band.Q.value = rand(0.7, 1.6);
          const gain = ctx.createGain();
          envelope(gain, rand(0.5, 0.9) * loudness, 0.004, rand(0.04, 0.09), start);
          src.connect(band).connect(gain).connect(out);
          src.start(start, rand(0, 0.35));
          src.stop(start + 0.14);
        }
      };
      const tone = (type, from, to, peak, length, filter) => {
        const osc = ctx.createOscillator();
        osc.type = type;
        osc.frequency.setValueAtTime(from, t);
        osc.frequency.exponentialRampToValueAtTime(to, t + length * 0.8);
        const gain = ctx.createGain();
        envelope(gain, peak, 0.008, length);
        let node = osc;
        if (filter) {
          const low = ctx.createBiquadFilter();
          low.type = 'lowpass';
          low.frequency.value = filter;
          node = osc.connect(low);
        }
        node.connect(gain).connect(out);
        osc.start(t);
        osc.stop(t + length + 0.05);
        return osc;
      };

      if (kind === 'chomp') crunch(4, 1);
      else if (kind === 'chew') crunch(3, 0.7);
      else if (kind === 'pickup') tone('triangle', 420, 820, 0.22, 0.1);
      else if (kind === 'gulp') tone('sine', 320, 70, 0.6, 0.22);
      else if (kind === 'miss') tone('square', 260, 110, 0.14, 0.18, 900);
      else if (kind === 'finale') {
        const osc = tone('sawtooth', 110, 68, 0.5, 0.45, 520);
        const wobble = ctx.createOscillator();
        wobble.frequency.value = 28;
        const depth = ctx.createGain();
        depth.gain.value = 18;
        wobble.connect(depth).connect(osc.frequency);
        wobble.start(t);
        wobble.stop(t + 0.55);
      }
    }
  }

  customElements.define('zombie-munch', ZombieMunch);
  // exposed so the 30-second game layer (zombie-munch-timed.js) can attach
  window.ZombieMunch = ZombieMunch;
})();
